import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components';
import css from './SetBackupPiecesModal.css';

export function SetBackupPiecesModal({ open, onOpenChange, product, totalActivePieces = 0, sdk }) {
  const queryClient = useQueryClient();

  // Read backup pieces count from managedPiecesData.backup
  const initialBackup = product?.attributes?.managedPiecesData?.backup ?? 0;

  const [backupCount, setBackupCount] = useState(initialBackup);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (open && product) {
      const currentBackup = product?.attributes?.managedPiecesData?.backup ?? 0;
      setBackupCount(currentBackup);
      setApiError(null);
    }
  }, [open, product]);

  // Preview box calculations
  const parsedBackup = Math.max(0, parseInt(backupCount, 10) || 0);
  const availableForClients = Math.max(0, totalActivePieces - parsedBackup);

  // Mutation to update backup count under managedPiecesData.backup
  const { mutate: handleSaveBackup, isLoading: isSubmitting } = useMutation({
    mutationFn: (newBackupValue) => {
      setApiError(null);
      const listingId = product?.id?.uuid || product?.id;

      const updatePayload = {
        id: listingId,
        managedPiecesData: {
          backup: newBackupValue
        }
      };

      return sdk.newSdk.listings.update(updatePayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ownListings']);
      onOpenChange(false);
    },
    onError: (error) => {
      console.error('Error while updating backup pieces count:', error);
      const rawError = error?.response?.data || error?.data || error;
      let errorMessage = '';

      if (typeof rawError === 'string') {
        errorMessage = rawError.trim();
      } else if (rawError?.errors?.[0]?.detail) {
        errorMessage = rawError.errors[0].detail;
      } else if (rawError?.message) {
        errorMessage = rawError.message;
      }

      setApiError(errorMessage || 'Wystąpił błąd podczas zapisywania liczby sztuk rezerwowych.');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!product || isSubmitting) return;

    handleSaveBackup(parsedBackup);
  };

  const productTitle = product?.attributes?.title || 'Produkt';

  return (
    <Modal
      isOpen={open}
      onClose={() => !isSubmitting && onOpenChange(false)}
      id="SetBackupPiecesModal"
      title="Ustawienie sztuk rezerwowych"
      onManageDisableScrolling={() => {}}
      usePortal
    >
      <div className={css.modalContentWrapper}>
        {/* Header title & product subtitle */}
        <div className={css.headerGroup}>
          <div className={css.titleRow}>
            <span className={css.gearIcon}>⚙️</span>
            <h2 className={css.modalTitle}>Ustawienie sztuk rezerwowych</h2>
          </div>
          <div className={css.productSubtitle}>Produkt: {productTitle}</div>
        </div>

        <form onSubmit={handleSubmit} className={css.modalForm}>
          {/* Main counter card */}
          <div className={css.counterCard}>
            <div className={css.counterRow}>
              <span className={css.counterLabel}>Liczba aktywnych sztuk:</span>
              <span className={css.activeCountValue}>{totalActivePieces} szt.</span>
            </div>

            <div className={css.counterRow}>
              <label htmlFor="backupInput" className={css.counterLabelBold}>
                Liczba sztuk rezerwowych:
              </label>
              <div className={css.inputGroup}>
                <input
                  id="backupInput"
                  type="number"
                  min="0"
                  max={totalActivePieces}
                  value={backupCount}
                  onChange={(e) => setBackupCount(e.target.value)}
                  disabled={isSubmitting}
                  className={css.numberInput}
                />
                <span className={css.unitText}>szt.</span>
              </div>
            </div>
          </div>

          <div className={css.sectionHeader}>CO TO OZNACZA?</div>

          {/* Blue Info Box: Available for online clients */}
          <div className={css.blueInfoBox}>
            <div className={css.infoBoxHeader}>
              <div className={css.infoTitleGroup}>
                <span className={css.infoIcon}>👥</span>
                <span className={css.blueTitle}>Liczba dostępna dla klientów:</span>
              </div>
              <span className={css.blueValue}>{availableForClients} szt.</span>
            </div>
            <p className={css.blueDescription}>
              Maksymalna liczba sztuk, które klienci mogą zarezerwować przez platformę Rent.
            </p>
          </div>

          {apiError && (
            <div className={css.errorBanner}>
              <span className={css.errorIcon}>⚠️</span>
              <div className={css.errorMessage}>{apiError}</div>
            </div>
          )}

          {/* Action buttons */}
          <div className={css.modalActions}>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => onOpenChange(false)}
              className={css.cancelBtn}
            >
              Anuluj
            </button>
            <button type="submit" disabled={isSubmitting} className={css.submitBtn}>
              {isSubmitting ? 'Zapisywanie...' : 'Zapisz ustawienia'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}